import React, { useState, useCallback } from "react";
import { Card, CardBody, Button } from "reactstrap";
import { useHistory, useParams } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HelpForm from "../HelpForm";
import { HelpItemPayload } from "../../types/HelpTypes";
import { useLocation } from "react-router-dom";
import ConfirmationModal from "../../../../globals/core/ConfirmationModal/ConfirmationModal";
import deleteButtonIcon from "../../../../assets/images/delete-button.svg";
import styles from "./index.module.scss";

export interface HelpEditorProps {
  onAdd?: (payload: HelpItemPayload) => void;
  onSave?: (payload: HelpItemPayload) => void;
  onDelete?: () => void;
  helpItem?: HelpItemPayload;
  loading?: boolean;
}

export const HelpEditor: React.FC<HelpEditorProps> = ({ onAdd, onSave, onDelete, helpItem, loading = false }) => {
  const isNew = useLocation().pathname.endsWith("/new");
  const params = useParams<{ id: string }>();
  const isEditing = !isNew && Boolean(params.id);
  const [itemWasChanged, setItemWasChanged] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const title = isNew ? "Add Help Item" : "Edit Help Item";
  const description = isNew
    ? "Create a help entry with a title, description and a PDF or video."
    : "Edit the help entry with a title, description and a PDF or video.";
  
  const history = useHistory();
  
  const handleGoBack = () => history.goBack();

  const handleFormChange = () => {
    setItemWasChanged(true);
  };

  const handleValidationChange = useCallback((valid: boolean) => {
    setIsFormValid(valid);
  }, []);

  const handleSaveClick = () => {
    // Trigger form submission
    const form = document.querySelector('form') as HTMLFormElement;
    if (form) {
      form.requestSubmit();
    }
  };

  const handleSave = (payload: HelpItemPayload) => {
    if (isEditing) {
      onSave && onSave(payload);
    } else {
      onAdd && onAdd(payload);
    }
    setItemWasChanged(false);
  };

  const handleDeleteClick = () => {
    setConfirmModalOpen(true);
  };

  const handleConfirmDelete = () => {
    setConfirmModalOpen(false);
    onDelete && onDelete();
  };

  const handleCancelDelete = () => {
    setConfirmModalOpen(false);
  };

  return (
    <>
    <div className="content">
      <div className="page-content">
        <div className="container-fluid">
          <div className="row mb-4">
            <div className="col-12">
              <Card>
                <CardBody>
                  <div className={styles.header}>
                    <Button className={styles.backButton} onClick={handleGoBack}>
                      <ArrowBackIcon />
                    </Button>
                    <div className={styles.headerText}>
                      <h3 className={styles.title}>{title}</h3>
                      <div className={styles.subtitle}>{description}</div>
                    </div>
                    <div className={styles.headerButtons}>
                      <div className={styles.deleteDialog}>
                        {isEditing && (
                          <Button className={styles.deleteButton} onClick={handleDeleteClick}>
                            <img src={deleteButtonIcon} alt="Delete Help Item" />
                          </Button>
                        )}
                      </div>
                      {itemWasChanged && (
                        <div className={styles.saveDialog}>
                          <Button 
                            className={styles.saveButton}
                            disabled={!isFormValid}
                            onClick={handleSaveClick}
                          >
                            Save Changes
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-3">Loading help item...</p>
                    </div>
                  ) : (
                    <HelpForm 
                      onAdd={onAdd}
                      onSave={handleSave}
                      onDelete={onDelete}
                      isEditing={isEditing}
                      initialData={helpItem}
                      onFormChange={handleFormChange}
                      onValidationChange={handleValidationChange}
                    />
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <ConfirmationModal
      open={confirmModalOpen}
      title="Delete Help Item"
      message="Are you sure you want to delete this help item?"
      onClose={handleCancelDelete}
      buttons={[
        { label: "Cancel", style: "info", loading: false, onClick: handleCancelDelete },
        { label: "Delete", style: "error", loading: false, onClick: handleConfirmDelete },
      ]}
    />
    </>
  );
};

export default HelpEditor;
